import { computed, reactive } from "vue";
import { defaultTechNotes } from "@/data/notes";
import { defaultLightVideos } from "@/data/lightVideos";
import { defaultAudioTracks } from "@/data/audioTracks";
import { defaultSoftwareLinks } from "@/data/softwareLinks";
import {
  createSearchItems,
  defaultNavGroups,
  defaultPortalCards,
} from "@/data/site";
import {
  extractBilibiliVideoParts,
  getBilibiliEmbedUrl,
} from "@/utils/videoEmbeds";
import { sortSoftwareLinksByName } from "@/utils/softwareSort";

const AUTH_STORAGE_KEY = "xfwebsite-server-auth-v1";
const defaultModuleIntros = {
  techNotes: {
    eyebrow: "技术手记 / Tech Notes",
    title: "把复杂过程整理成可读、可复用、可回看的教程。",
    description:
      "这里收录我的开发实战、问题排查、工具组合与创作流程。内容会以图文、代码片段、视频讲解等形式呈现，更适合被系统地阅读，也适合在以后反复查阅。",
    tags: ["图文教程", "视频拆解", "代码示例", "持续更新"],
  },
  lightVideos: {
    eyebrow: "光影音域 / Light & Sound",
    title: "把做过的视频作品收在这里，让画面、声音和创作痕迹都有一个可回看的入口。",
    description:
      "这里主要存放来自 B 站的视频作品链接。后台只需要填入视频地址，前台会自动生成内嵌播放器，适合整理成片、练习、项目记录和阶段性作品集。",
    tags: ["B 站内嵌播放", "作品分类", "视频归档", "持续更新"],
  },
  soundBlank: {
    eyebrow: "声音留白 / Sound Blank",
    title: "把旋律和情绪放在这里，让声音有一处可以停靠的地方。",
    description:
      "这里不做复杂的封面和详情页，只保留一首首可以直接播放的歌曲。后台填入音频链接后，前台会自动生成播放块，并接入底部控制面板。",
    tags: ["歌曲播放", "链接入库", "底部控制", "持续更新"],
  },
  commonSoftware: {
    eyebrow: "常用软件 / Common Software",
    title: "把常用工具和下载入口整理成一个轻便的软件抽屉。",
    description:
      "这里用于收纳高频软件、官方下载页和内部下载链接。后台上传 Logo、填写名称和网址后，前台会自动生成可点击的软件卡片。",
    tags: ["软件下载", "Logo 卡片", "后台维护", "快速入口"],
  },
};
const ARTICLE_TEXT_TEMPLATE = `# 这里填写文章标题

先用一两段话说明这篇文章要讲什么、适合谁看、看完能得到什么。

## 第一部分

直接像写文档一样输入正文内容即可。空一行会自动分段，不需要自己写 HTML。

## 第二部分

如果你要列清单，可以这样写：
- 第一项
- 第二项
- 第三项

## 代码示例

\`\`\`js
console.log("Hello, world!");
\`\`\`

## 配图说明

如果正文里还要插入图片，可以写：
![这里填写图片说明](/images/portal-info.webp)
`;

let uid = 0;
let loadPromise = null;

const clone = (value) => JSON.parse(JSON.stringify(value));
const createId = (prefix) => `${prefix}-${Date.now().toString(36)}-${uid++}`;
const createBlockId = (type) => createId(`block-${type}`);

const slugify = (value = "") => {
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || `note-${Date.now().toString(36)}`;
};

const makeStableSlug = (value = "", fallback = "item") => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || fallback;
};

const ensureUniqueSlug = (slug, notes, currentId) => {
  const baseSlug = slugify(slug);
  let nextSlug = baseSlug;
  let counter = 2;

  while (notes.some((note) => note.id !== currentId && note.slug === nextSlug)) {
    nextSlug = `${baseSlug}-${counter}`;
    counter += 1;
  }

  return nextSlug;
};

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const renderInlineText = (value = "") => {
  const escaped = escapeHtml(value);

  return escaped
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
};

const extractYoutubeId = (url = "") => {
  const match = String(url).match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/i
  );

  return match?.[1] || "";
};

const createEmptyContentBlock = (type = "paragraph") => {
  switch (type) {
    case "heading":
      return {
        id: createBlockId("heading"),
        type: "heading",
        level: 2,
        text: "新的小标题",
      };
    case "image":
      return {
        id: createBlockId("image"),
        type: "image",
        src: "/images/portal-info.webp",
        alt: "文章配图",
        caption: "这里填写图片说明",
      };
    case "video":
      return {
        id: createBlockId("video"),
        type: "video",
        src: "",
        caption: "这里填写视频说明",
      };
    case "code":
      return {
        id: createBlockId("code"),
        type: "code",
        language: "js",
        filename: "example.js",
        code: 'console.log("Hello, world!");',
      };
    case "callout":
      return {
        id: createBlockId("callout"),
        type: "callout",
        tone: "info",
        title: "重点提示",
        text: "这里填写想强调的说明内容。",
      };
    case "list":
      return {
        id: createBlockId("list"),
        type: "list",
        items: ["第一项", "第二项", "第三项"],
      };
    default:
      return {
        id: createBlockId("paragraph"),
        type: "paragraph",
        text: "这里填写正文内容。",
      };
  }
};

const normalizeContentBlocks = (input = []) =>
  (Array.isArray(input) ? input : [])
    .map((block) => {
      if (!block || !block.type) {
        return null;
      }

      if (block.type === "heading") {
        return {
          ...createEmptyContentBlock("heading"),
          ...block,
          id: block.id || createBlockId("heading"),
          level: Number(block.level) >= 3 ? 3 : 2,
          text: String(block.text || "").trim() || "新的小标题",
        };
      }

      if (block.type === "paragraph") {
        return {
          ...createEmptyContentBlock("paragraph"),
          ...block,
          id: block.id || createBlockId("paragraph"),
          text: String(block.text || ""),
        };
      }

      if (block.type === "list") {
        return {
          ...createEmptyContentBlock("list"),
          ...block,
          id: block.id || createBlockId("list"),
          items: (Array.isArray(block.items) ? block.items : []).map((item) =>
            String(item || "")
          ),
        };
      }

      if (block.type === "image") {
        return {
          ...createEmptyContentBlock("image"),
          ...block,
          id: block.id || createBlockId("image"),
          src: String(block.src || ""),
          alt: String(block.alt || ""),
          caption: String(block.caption || ""),
        };
      }

      if (block.type === "video") {
        return {
          ...createEmptyContentBlock("video"),
          ...block,
          id: block.id || createBlockId("video"),
          src: String(block.src || ""),
          caption: String(block.caption || ""),
        };
      }

      if (block.type === "code") {
        return {
          ...createEmptyContentBlock("code"),
          ...block,
          id: block.id || createBlockId("code"),
          language: String(block.language || "txt"),
          filename: String(block.filename || ""),
          code: String(block.code || ""),
        };
      }

      if (block.type === "callout") {
        return {
          ...createEmptyContentBlock("callout"),
          ...block,
          id: block.id || createBlockId("callout"),
          tone: ["info", "tip", "warning"].includes(block.tone) ? block.tone : "info",
          title: String(block.title || ""),
          text: String(block.text || ""),
        };
      }

      return null;
    })
    .filter(Boolean);

const normalizeContentBlocksClean = (input = []) =>
  normalizeContentBlocks(input).map((block) => {
    if (!block) {
      return null;
    }

    if (block.type === "image" || block.type === "video") {
      return {
        id: block.id,
        type: block.type,
        src: String(block.src || ""),
        alt: String(block.alt || ""),
        caption: String(block.caption || ""),
        width: Math.min(100, Math.max(20, Number.parseInt(block.width || "100", 10) || 100)),
        align: ["left", "center", "right"].includes(block.align) ? block.align : "center",
        inRow: Boolean(block.inRow),
      };
    }

    if (block.type === "heading") {
      return {
        id: block.id,
        type: "heading",
        level: Number(block.level) >= 3 ? 3 : 2,
        text: String(block.text || "").trim() || "新的小标题",
      };
    }

    if (block.type === "paragraph") {
      return {
        id: block.id,
        type: "paragraph",
        text: String(block.text || ""),
      };
    }

    if (block.type === "list") {
      return {
        id: block.id,
        type: "list",
        items: (Array.isArray(block.items) ? block.items : []).map((item) =>
          String(item || "")
        ),
      };
    }

    if (block.type === "code") {
      return {
        id: block.id,
        type: "code",
        language: String(block.language || "txt"),
        filename: String(block.filename || ""),
        code: String(block.code || ""),
      };
    }

    if (block.type === "callout") {
      return {
        id: block.id,
        type: "callout",
        tone: ["info", "tip", "warning"].includes(block.tone) ? block.tone : "info",
        title: String(block.title || ""),
        text: String(block.text || ""),
      };
    }

    return null;
  }).filter(Boolean);

const renderContentBlocksToHtml = (contentBlocks = []) => {
  const normalizedBlocks = normalizeContentBlocksClean(contentBlocks);

  if (!normalizedBlocks.length) {
    return '<div class="article-empty">这篇文章还没有正文内容。</div>';
  }

  return normalizedBlocks
    .map((block) => {
      if (block.type === "heading") {
        const tag = Number(block.level) >= 3 ? "h3" : "h2";
        return `<${tag}>${renderInlineText(block.text || "")}</${tag}>`;
      }

      if (block.type === "paragraph") {
        return `<p>${renderInlineText(block.text || "").replace(/\n/g, "<br>")}</p>`;
      }

      if (block.type === "list") {
        return `<ul>${(block.items || [])
          .map((item) => `<li>${renderInlineText(item || "")}</li>`)
          .join("")}</ul>`;
      }

      if (block.type === "image") {
        return `
          <figure class="article-figure">
            <img src="${escapeHtml(block.src || "")}" alt="${escapeHtml(
              block.alt || block.caption || "文章配图"
            )}">
            ${
              block.caption
                ? `<figcaption>${renderInlineText(block.caption)}</figcaption>`
                : ""
            }
          </figure>
        `;
      }

      if (block.type === "video") {
        const src = String(block.src || "").trim();
        const youtubeId = extractYoutubeId(src);
        const mediaHtml = youtubeId
          ? `
            <iframe
              class="article-video-embed"
              src="https://www.youtube.com/embed/${youtubeId}"
              title="文章视频"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
          `
          : `<video class="article-video-player" controls preload="metadata" src="${escapeHtml(
              src
            )}"></video>`;

        return `
          <section class="article-video">
            <div class="article-video-frame">${mediaHtml}</div>
            ${
              block.caption
                ? `<p class="article-video-note">${renderInlineText(block.caption)}</p>`
                : ""
            }
          </section>
        `;
      }

      if (block.type === "code") {
        const code = String(block.code || "");
        return `
          <section class="article-code">
            <div class="article-code-head">
              <span>${escapeHtml(block.filename || block.language || "code")}</span>
              <button type="button" data-copy-code="${escapeHtml(code)}">复制代码</button>
            </div>
            <pre><code>${escapeHtml(code)}</code></pre>
          </section>
        `;
      }

      if (block.type === "callout") {
        const tone = ["info", "tip", "warning"].includes(block.tone)
          ? block.tone
          : "info";
        return `
          <div class="article-callout is-${tone}">
            ${block.title ? `<strong>${renderInlineText(block.title)}</strong>` : ""}
            ${block.text ? `<p>${renderInlineText(block.text).replace(/\n/g, "<br>")}</p>` : ""}
          </div>
        `;
      }

      return "";
    })
    .join("\n");
};

const editorTextToHtml = (input = "") => {
  const normalized = String(input || "").replace(/\r\n/g, "\n").trim();

  if (!normalized) {
    return '<div class="article-empty">这篇文章还没有正文内容。</div>';
  }

  const lines = normalized.split("\n");
  const blocks = [];
  let index = 0;

  const pushParagraph = (paragraphLines) => {
    const text = paragraphLines.join(" ").trim();
    if (text) {
      blocks.push(`<p>${renderInlineText(text)}</p>`);
    }
  };

  while (index < lines.length) {
    const line = lines[index].trim();

    if (!line) {
      index += 1;
      continue;
    }

    if (line.startsWith("```")) {
      const fileLabel = line.slice(3).trim() || "code";
      const codeLines = [];
      index += 1;

      while (index < lines.length && !lines[index].trim().startsWith("```")) {
        codeLines.push(lines[index]);
        index += 1;
      }

      if (index < lines.length) {
        index += 1;
      }

      blocks.push(
        renderContentBlocksToHtml([
          {
            ...createEmptyContentBlock("code"),
            filename: fileLabel,
            language: fileLabel,
            code: codeLines.join("\n"),
          },
        ])
      );
      continue;
    }

    const imageMatch = line.match(/^!\[(.*)\]\((.+)\)$/);
    if (imageMatch) {
      blocks.push(
        renderContentBlocksToHtml([
          {
            ...createEmptyContentBlock("image"),
            src: imageMatch[2].trim(),
            alt: imageMatch[1].trim(),
            caption: imageMatch[1].trim(),
          },
        ])
      );
      index += 1;
      continue;
    }

    const videoMatch = line.match(/^\[(?:视频|video)\]\((.+)\)$/i);
    if (videoMatch) {
      blocks.push(
        renderContentBlocksToHtml([
          {
            ...createEmptyContentBlock("video"),
            src: videoMatch[1].trim(),
          },
        ])
      );
      index += 1;
      continue;
    }

    if (line.startsWith("### ")) {
      blocks.push(`<h3>${renderInlineText(line.slice(4).trim())}</h3>`);
      index += 1;
      continue;
    }

    if (line.startsWith("## ") || line.startsWith("# ")) {
      blocks.push(`<h2>${renderInlineText(line.replace(/^##?\s/, "").trim())}</h2>`);
      index += 1;
      continue;
    }

    if (line.startsWith(">")) {
      const calloutLines = [];
      while (index < lines.length && lines[index].trim().startsWith(">")) {
        calloutLines.push(lines[index].trim().replace(/^>\s?/, ""));
        index += 1;
      }
      blocks.push(
        renderContentBlocksToHtml([
          {
            ...createEmptyContentBlock("callout"),
            text: calloutLines.join("\n"),
          },
        ])
      );
      continue;
    }

    if (line.startsWith("- ")) {
      const items = [];
      while (index < lines.length && lines[index].trim().startsWith("- ")) {
        items.push(lines[index].trim().slice(2).trim());
        index += 1;
      }
      blocks.push(
        renderContentBlocksToHtml([
          {
            ...createEmptyContentBlock("list"),
            items,
          },
        ])
      );
      continue;
    }

    const paragraphLines = [];
    while (index < lines.length) {
      const currentLine = lines[index].trim();
      if (
        !currentLine ||
        currentLine.startsWith("```") ||
        currentLine.startsWith("### ") ||
        currentLine.startsWith("## ") ||
        currentLine.startsWith("# ") ||
        currentLine.startsWith(">") ||
        currentLine.startsWith("- ") ||
        /^!\[(.*)\]\((.+)\)$/.test(currentLine) ||
        /^\[(?:视频|video)\]\((.+)\)$/i.test(currentLine)
      ) {
        break;
      }

      paragraphLines.push(currentLine);
      index += 1;
    }

    pushParagraph(paragraphLines);
  }

  return blocks.join("\n");
};

const htmlToEditorText = (html = "") => {
  const source = String(html || "").trim();
  if (!source) {
    return ARTICLE_TEXT_TEMPLATE;
  }

  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return source
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n\n")
      .replace(/<[^>]+>/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  const parser = new DOMParser();
  const documentFragment = parser.parseFromString(source, "text/html");
  const blocks = [];

  const pushText = (text = "") => {
    const normalized = text.replace(/\s+/g, " ").trim();
    if (normalized) {
      blocks.push(normalized);
    }
  };

  Array.from(documentFragment.body.children).forEach((node) => {
    const tag = node.tagName.toLowerCase();

    if (tag === "h2") {
      pushText(`## ${node.textContent || ""}`);
      return;
    }

    if (tag === "h3") {
      pushText(`### ${node.textContent || ""}`);
      return;
    }

    if (tag === "p") {
      pushText(node.textContent || "");
      return;
    }

    if (tag === "ul" || tag === "ol") {
      Array.from(node.querySelectorAll("li")).forEach((item) => {
        pushText(`- ${item.textContent || ""}`);
      });
      return;
    }

    if (tag === "figure") {
      const image = node.querySelector("img");
      const caption = node.querySelector("figcaption")?.textContent?.trim() || "图片说明";
      if (image?.getAttribute("src")) {
        pushText(`![${caption}](${image.getAttribute("src")})`);
      }
      return;
    }

    if (tag === "section" && node.classList.contains("article-code")) {
      const fileLabel =
        node.querySelector(".article-code-head span")?.textContent?.trim() || "code";
      const code = node.querySelector("pre code")?.textContent || "";
      pushText(`\`\`\`${fileLabel}\n${code.trim()}\n\`\`\``);
      return;
    }

    if (tag === "section" && node.classList.contains("article-video")) {
      const iframeSrc = node.querySelector("iframe")?.getAttribute("src");
      const videoSrc = node.querySelector("video")?.getAttribute("src");
      const mediaSrc = iframeSrc || videoSrc;
      if (mediaSrc) {
        pushText(`[视频](${mediaSrc})`);
      }
      return;
    }

    if (tag === "div" && node.classList.contains("article-callout")) {
      pushText(`> ${node.textContent || ""}`);
      return;
    }

    pushText(node.textContent || "");
  });

  return blocks.join("\n\n").trim() || ARTICLE_TEXT_TEMPLATE;
};

const htmlToContentBlocks = (html = "") => {
  const source = String(html || "").trim();
  if (!source) {
    return [
      {
        ...createEmptyContentBlock("heading"),
        text: "这里填写文章标题",
      },
      {
        ...createEmptyContentBlock("paragraph"),
        text: "这里填写文章导语，让读者知道这篇内容会解决什么问题。",
      },
    ];
  }

  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return [
      {
        ...createEmptyContentBlock("paragraph"),
        text: source.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim(),
      },
    ];
  }

  const parser = new DOMParser();
  const documentFragment = parser.parseFromString(source, "text/html");
  const blocks = [];

  Array.from(documentFragment.body.children).forEach((node) => {
    const tag = node.tagName.toLowerCase();

    if (tag === "h2" || tag === "h3") {
      blocks.push({
        ...createEmptyContentBlock("heading"),
        level: tag === "h3" ? 3 : 2,
        text: node.textContent?.trim() || "新的小标题",
      });
      return;
    }

    if (tag === "p") {
      blocks.push({
        ...createEmptyContentBlock("paragraph"),
        text: node.textContent?.trim() || "",
      });
      return;
    }

    if (tag === "ul" || tag === "ol") {
      blocks.push({
        ...createEmptyContentBlock("list"),
        items: Array.from(node.querySelectorAll("li")).map((item) =>
          item.textContent?.trim() || ""
        ),
      });
      return;
    }

    if (tag === "figure") {
      const image = node.querySelector("img");
      if (image?.getAttribute("src")) {
        blocks.push({
          ...createEmptyContentBlock("image"),
          src: image.getAttribute("src") || "",
          alt: image.getAttribute("alt") || "",
          caption: node.querySelector("figcaption")?.textContent?.trim() || "",
          width: Number.parseInt(
            image.style.width || node.getAttribute("data-width") || "100",
            10
          ) || 100,
        });
      }
      return;
    }

    if (tag === "section" && node.classList.contains("article-video")) {
      const iframeSrc = node.querySelector("iframe")?.getAttribute("src");
      const videoSrc = node.querySelector("video")?.getAttribute("src");
      blocks.push({
        ...createEmptyContentBlock("video"),
        src: iframeSrc || videoSrc || "",
        caption: node.querySelector(".article-video-note")?.textContent?.trim() || "",
      });
      return;
    }

    if (tag === "section" && node.classList.contains("article-code")) {
      blocks.push({
        ...createEmptyContentBlock("code"),
        filename:
          node.querySelector(".article-code-head span")?.textContent?.trim() || "code",
        code: node.querySelector("pre code")?.textContent || "",
      });
      return;
    }

    if (tag === "div" && node.classList.contains("article-callout")) {
      const toneClass = Array.from(node.classList).find((item) => item.startsWith("is-"));
      blocks.push({
        ...createEmptyContentBlock("callout"),
        tone: toneClass ? toneClass.replace("is-", "") : "info",
        title: node.querySelector("strong")?.textContent?.trim() || "",
        text: Array.from(node.querySelectorAll("p"))
          .map((item) => item.textContent?.trim() || "")
          .filter(Boolean)
          .join("\n\n"),
      });
    }
  });

  return normalizeContentBlocksClean(blocks);
};

const countCodeBlocks = (contentText = "") =>
  (String(contentText || "").match(/```/g) || []).length / 2;

const countCodeBlocksInContentBlocks = (contentBlocks = []) =>
  normalizeContentBlocksClean(contentBlocks).filter((block) => block.type === "code").length;

const normalizeNavGroups = (input = defaultNavGroups) => ({
  left: (input.left || []).map((group, groupIndex) => ({
    ...group,
    id: group.id || createId(`nav-left-${groupIndex}`),
    key: group.key || slugify(group.full || `left-${groupIndex}`),
    links: (group.links || []).map((link, linkIndex) => ({
      ...link,
      id: link.id || createId(`nav-link-${groupIndex}-${linkIndex}`),
    })),
  })),
  right: (input.right || []).map((group, groupIndex) => ({
    ...group,
    id: group.id || createId(`nav-right-${groupIndex}`),
    key: group.key || slugify(group.full || `right-${groupIndex}`),
    links: (group.links || []).map((link, linkIndex) => ({
      ...link,
      id: link.id || createId(`nav-link-${groupIndex}-${linkIndex}`),
    })),
  })),
});

const introValue = (input = {}, key, fallback = {}) =>
  Object.prototype.hasOwnProperty.call(input, key)
    ? String(input[key] ?? "")
    : String(fallback[key] ?? "");

const normalizeIntro = (input = {}, fallback = {}) => ({
  eyebrow: introValue(input, "eyebrow", fallback),
  title: introValue(input, "title", fallback),
  description: introValue(input, "description", fallback),
  tags: (Array.isArray(input.tags) ? input.tags : fallback.tags || [])
    .map((tag) => String(tag || "").trim())
    .filter(Boolean)
    .slice(0, 8),
});

const normalizeModuleIntros = (input = defaultModuleIntros) => ({
  techNotes: normalizeIntro(input.techNotes, defaultModuleIntros.techNotes),
  lightVideos: normalizeIntro(input.lightVideos, defaultModuleIntros.lightVideos),
  soundBlank: normalizeIntro(input.soundBlank, defaultModuleIntros.soundBlank),
  commonSoftware: normalizeIntro(input.commonSoftware, defaultModuleIntros.commonSoftware),
});

const normalizeTechNotes = (input = defaultTechNotes) => {
  const notes = (input || []).map((note, index) => {
    const contentText =
      (note.contentText || htmlToEditorText(note.contentHtml || "")).trim() ||
      ARTICLE_TEXT_TEMPLATE;
    const contentBlocks = normalizeContentBlocksClean(
      note.contentBlocks?.length
        ? note.contentBlocks
        : htmlToContentBlocks(note.contentHtml || editorTextToHtml(contentText))
    );
    const contentHtml = String(note.contentHtml || "").trim() || renderContentBlocksToHtml(contentBlocks);

    return {
      ...note,
      id: note.id || createId(`note-${index}`),
      title: note.title || "未命名文章",
      summary: note.summary || "",
      heroSummary: note.heroSummary || "",
      image: note.image || "/images/portal-info.webp",
      date: note.date || new Date().toISOString().slice(0, 10),
      reading: note.reading || "阅读时间 5 分钟",
      categoryTitle: note.categoryTitle || "未分类",
      categorySlug:
        note.categorySlug || slugify(note.categoryTitle || "uncategorized"),
      contentText,
      contentBlocks,
      contentHtml,
      codeBlocks:
        Number(note.codeBlocks) || countCodeBlocksInContentBlocks(contentBlocks),
      slug: note.slug || slugify(note.title || `note-${index + 1}`),
    };
  });

  return notes.map((note) => ({
    ...note,
    slug: ensureUniqueSlug(note.slug, notes, note.id),
  }));
};

const normalizeLightVideos = (input = defaultLightVideos) => {
  const videos = (input || []).map((video, index) => {
    const sourceUrl = String(video.sourceUrl || video.videoUrl || "").trim();
    const parts = extractBilibiliVideoParts(sourceUrl);
    const page = Math.max(1, Number.parseInt(parts.page || "1", 10) || 1);
    const sourceSlug = parts.bvid || (parts.aid ? `av-${parts.aid}` : "");
    const fallbackSlug =
      video.slug || `light-video-${index + 1}-${Date.now().toString(36)}`;
    const description = video.heroSummary || video.summary || "";

    return {
      ...video,
      id: video.id || createId(`light-video-${index}`),
      slug: makeStableSlug(sourceSlug || video.title, fallbackSlug),
      title: video.title || "未命名视频",
      summary: description,
      heroSummary: description,
      date: video.date || new Date().toISOString().slice(0, 10),
      duration: video.duration || "视频作品",
      categorySlug:
        video.categorySlug || makeStableSlug(video.categoryTitle, "video-work"),
      categoryTitle: video.categoryTitle || "未分类",
      sourceUrl,
      bvid: video.bvid || parts.bvid,
      aid: video.aid || parts.aid,
      page,
      embedUrl:
        video.embedUrl ||
        getBilibiliEmbedUrl(sourceUrl, {
          page,
        }),
    };
  });

  return videos.map((video) => ({
    ...video,
    slug: ensureUniqueSlug(video.slug, videos, video.id),
  }));
};

const normalizeAudioTracks = (input = defaultAudioTracks) => {
  const tracks = (input || []).map((track, index) => {
    const title = String(track.title || "未命名歌曲").trim() || "未命名歌曲";
    const artist = String(track.artist || "").trim();
    const audioUrl = String(track.audioUrl || track.sourceUrl || "").trim();
    const lyricsUrl = String(track.lyricsUrl || track.lyricUrl || "").trim();

    return {
      ...track,
      id: track.id || createId(`audio-track-${index}`),
      slug: makeStableSlug(track.slug || title, `audio-track-${index + 1}`),
      title,
      artist,
      summary: String(track.summary || ""),
      date: track.date || new Date().toISOString().slice(0, 10),
      duration: track.duration || "音频作品",
      categorySlug:
        track.categorySlug || makeStableSlug(track.categoryTitle, "music"),
      categoryTitle: track.categoryTitle || "音乐",
      audioUrl,
      lyricsUrl,
    };
  });

  return tracks.map((track) => ({
    ...track,
    slug: ensureUniqueSlug(track.slug, tracks, track.id),
  }));
};

const normalizeSoftwareLinks = (input = defaultSoftwareLinks) =>
  sortSoftwareLinksByName((Array.isArray(input) ? input : [])
    .map((software, index) => {
      const name = String(software.name || "未命名软件").trim() || "未命名软件";

      return {
        ...software,
        id: software.id || createId(`software-${index}`),
        name,
        url: String(software.url || software.href || "").trim(),
        logo: String(software.logo || software.image || "").trim(),
        isPublic: software.isPublic !== false,
      };
    })
    .filter((software) => software.name || software.url || software.logo));

const normalizeContentPayload = (payload = {}) => ({
  navGroups: normalizeNavGroups(payload.navGroups),
  moduleIntros: normalizeModuleIntros(payload.moduleIntros),
  techNotes: normalizeTechNotes(payload.techNotes),
  lightVideos: normalizeLightVideos(payload.lightVideos),
  audioTracks: normalizeAudioTracks(payload.audioTracks),
  softwareLinks: normalizeSoftwareLinks(payload.softwareLinks),
});

const getDefaultContentSnapshot = () =>
  normalizeContentPayload({
    navGroups: defaultNavGroups,
    moduleIntros: defaultModuleIntros,
    techNotes: defaultTechNotes,
    lightVideos: defaultLightVideos,
    audioTracks: defaultAudioTracks,
    softwareLinks: defaultSoftwareLinks,
  });

const getAuthToken = () => {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.token || "";
  } catch (error) {
    return "";
  }
};

const request = async (path, options = {}) => {
  try {
    const headers = {
      ...(options.headers || {}),
    };

    if (options.body !== undefined) {
      headers["Content-Type"] = "application/json";
    }

    if (options.auth !== false) {
      const token = getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await fetch(path, {
      method: options.method || "GET",
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });

    const contentType = response.headers.get("content-type") || "";
    const payload = contentType.includes("application/json")
      ? await response.json()
      : null;

    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        error: payload?.error || "内容请求失败，请稍后重试。",
      };
    }

    return {
      ok: true,
      status: response.status,
      data: payload,
    };
  } catch (error) {
    return {
      ok: false,
      status: 0,
      error: "无法连接到内容服务，请确认本地后端已启动。",
    };
  }
};

const NON_CONVERTIBLE_IMAGE_TYPES = new Set(["image/gif", "image/svg+xml"]);
const MAX_UPLOAD_IMAGE_DIMENSION = 1920;
const WEBP_UPLOAD_QUALITY = 0.82;

const shouldConvertImageToWebp = (file) =>
  Boolean(
    file &&
      typeof file.type === "string" &&
      file.type.startsWith("image/") &&
      file.type !== "image/webp" &&
      !NON_CONVERTIBLE_IMAGE_TYPES.has(file.type)
  );

const loadImageSource = async (file) => {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        dispose: () => bitmap.close?.(),
      };
    } catch (error) {
      // Fall back to HTMLImageElement loading below.
    }
  }

  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      resolve({
        source: image,
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
        dispose: () => URL.revokeObjectURL(objectUrl),
      });
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Image load failed"));
    };

    image.src = objectUrl;
  });
};

const convertImageFileToWebp = async (file) => {
  if (!shouldConvertImageToWebp(file)) {
    return file;
  }

  const loadedImage = await loadImageSource(file);

  try {
    const longestSide = Math.max(loadedImage.width, loadedImage.height, 1);
    const scale = Math.min(1, MAX_UPLOAD_IMAGE_DIMENSION / longestSide);
    const targetWidth = Math.max(1, Math.round(loadedImage.width * scale));
    const targetHeight = Math.max(1, Math.round(loadedImage.height * scale));
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      return file;
    }

    canvas.width = targetWidth;
    canvas.height = targetHeight;
    context.drawImage(loadedImage.source, 0, 0, targetWidth, targetHeight);

    const webpBlob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/webp", WEBP_UPLOAD_QUALITY)
    );

    if (!webpBlob) {
      return file;
    }

    const originalBaseName = String(file.name || "image").replace(/\.[^.]+$/, "");
    return new File([webpBlob], `${originalBaseName}.webp`, {
      type: "image/webp",
      lastModified: Date.now(),
    });
  } finally {
    loadedImage.dispose?.();
  }
};

const prepareMediaFileForUpload = async (file) => {
  if (!file || typeof file.type !== "string") {
    return file;
  }

  if (!file.type.startsWith("image/")) {
    return file;
  }

  try {
    return await convertImageFileToWebp(file);
  } catch (error) {
    return file;
  }
};

const uploadMediaFile = async (file) => {
  try {
    const preparedFile = await prepareMediaFileForUpload(file);
    const formData = new FormData();
    formData.append("file", preparedFile);

    const headers = {};
    const token = getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch("/api/admin/media", {
      method: "POST",
      headers,
      body: formData,
    });

    const contentType = response.headers.get("content-type") || "";
    const payload = contentType.includes("application/json")
      ? await response.json()
      : null;

    if (!response.ok) {
      return {
        ok: false,
        status: response.status,
        error: payload?.error || "媒体上传失败，请稍后重试。",
      };
    }

    if (!payload?.url) {
      return {
        ok: false,
        status: response.status,
        error: "上传接口返回异常，请确认本地后端已更新。",
      };
    }

    return {
      ok: true,
      status: response.status,
      data: payload,
    };
  } catch (error) {
    return {
      ok: false,
      status: 0,
      error: "无法连接到上传服务，请确认本地后端已启动。",
    };
  }
};

const state = reactive({
  navGroups: normalizeNavGroups(),
  moduleIntros: normalizeModuleIntros(),
  techNotes: normalizeTechNotes(),
  lightVideos: normalizeLightVideos(),
  audioTracks: normalizeAudioTracks(),
  softwareLinks: normalizeSoftwareLinks(),
  ready: false,
  loading: false,
  saving: false,
  error: "",
});

const applyContent = (payload) => {
  const normalized = normalizeContentPayload(payload);
  state.navGroups = normalized.navGroups;
  state.moduleIntros = normalized.moduleIntros;
  state.techNotes = normalized.techNotes;
  state.lightVideos = normalized.lightVideos;
  state.audioTracks = normalized.audioTracks;
  state.softwareLinks = normalized.softwareLinks;
};

const createEmptyNavGroup = (side) => ({
  id: createId(`nav-group-${side}`),
  key: slugify(`${side}-group-${Date.now()}`),
  full: "新导航组",
  short: "新组",
  links: [],
});

const createEmptyNavLink = () => ({
  id: createId("nav-link"),
  text: "新链接",
  href: "https://",
});

const createEmptyTechNote = (notes = []) => {
  const contentText = ARTICLE_TEXT_TEMPLATE;
  const contentBlocks = [
    {
      ...createEmptyContentBlock("heading"),
      text: "先写一个明确的小标题",
    },
    {
      ...createEmptyContentBlock("paragraph"),
      text: "这里直接写正文内容。你可以把每一段拆成独立内容块，图片、视频、代码也都能单独添加。",
    },
    {
      ...createEmptyContentBlock("image"),
      caption: "这里可以放一张正文配图",
    },
    {
      ...createEmptyContentBlock("code"),
      filename: "example.js",
      code: 'console.log("Block editor ready");',
    },
  ];

  return {
    id: createId("note"),
    slug: ensureUniqueSlug(`note-${Date.now().toString(36)}`, notes),
    title: "新技术手记",
    summary: "",
    heroSummary: "请填写详情页顶部的文章说明。",
    image: "/images/portal-info.webp",
    date: new Date().toISOString().slice(0, 10),
    reading: "阅读时间 5 分钟",
    categorySlug: "uncategorized",
    categoryTitle: "未分类",
    contentText,
    contentBlocks,
    contentHtml: renderContentBlocksToHtml(contentBlocks),
    codeBlocks: countCodeBlocksInContentBlocks(contentBlocks),
  };
};

const createEmptyLightVideo = (videos = []) => {
  const title = "新的光影音域视频";

  return {
    id: createId("light-video"),
    slug: ensureUniqueSlug(`light-video-${Date.now().toString(36)}`, videos),
    title,
    summary: "这里填写视频简介，可以写创作背景、制作工具或成片亮点。",
    heroSummary: "这里填写视频简介，可以写创作背景、制作工具或成片亮点。",
    date: new Date().toISOString().slice(0, 10),
    duration: "视频作品",
    categorySlug: "video-work",
    categoryTitle: "视频作品",
    sourceUrl: "",
    bvid: "",
    aid: "",
    page: 1,
    embedUrl: "",
  };
};

const createEmptyAudioTrack = (tracks = []) => {
  const title = "新的声音留白歌曲";

  return {
    id: createId("audio-track"),
    slug: ensureUniqueSlug(`audio-track-${Date.now().toString(36)}`, tracks),
    title,
    artist: "",
    summary: "这里填写歌曲简介或想记录的声音备注。",
    date: new Date().toISOString().slice(0, 10),
    duration: "音频作品",
    categorySlug: "music",
    categoryTitle: "音乐",
    audioUrl: "",
    lyricsUrl: "",
  };
};

const createEmptySoftwareLink = () => ({
  id: createId("software"),
  name: "新的常用软件",
  url: "https://",
  logo: "/images/software/chrome.png",
  isPublic: true,
});

export const useContentStore = () => {
  const loadContent = async () => {
    state.loading = true;
    const result = await request("/api/content");
    state.loading = false;

    if (!result.ok) {
      state.error = result.error;
      state.ready = true;
      return result;
    }

    applyContent(result.data);
    state.error = "";
    state.ready = true;
    return result;
  };

  const ensureContentLoaded = async () => {
    if (state.ready) {
      return {
        ok: true,
      };
    }

    if (loadPromise) {
      return loadPromise;
    }

    loadPromise = loadContent().finally(() => {
      loadPromise = null;
    });

    return loadPromise;
  };

  const createContentSnapshot = () =>
    clone({
      navGroups: state.navGroups,
      moduleIntros: state.moduleIntros,
      techNotes: state.techNotes,
      lightVideos: state.lightVideos,
      audioTracks: state.audioTracks,
      softwareLinks: state.softwareLinks,
    });

  const saveContentDraft = async (payload) => {
    const normalized = normalizeContentPayload(payload);

    state.saving = true;
    const result = await request("/api/admin/content", {
      method: "PUT",
      body: normalized,
    });
    state.saving = false;

    if (!result.ok) {
      state.error = result.error;
      return result;
    }

    state.error = "";
    applyContent(result.data);
    return {
      ok: true,
      data: createContentSnapshot(),
    };
  };

  return {
    state,
    navGroups: computed(() => state.navGroups),
    moduleIntros: computed(() => state.moduleIntros),
    techNotes: computed(() => state.techNotes),
    lightVideos: computed(() => state.lightVideos),
    audioTracks: computed(() => state.audioTracks),
    softwareLinks: computed(() => state.softwareLinks),
    portalCards: computed(() => clone(defaultPortalCards)),
    searchItems: computed(() =>
      createSearchItems(
        state.navGroups,
        state.techNotes,
        state.lightVideos,
        state.audioTracks,
        state.softwareLinks
      )
    ),
    contentReady: computed(() => state.ready),
    loadingContent: computed(() => state.loading),
    savingContent: computed(() => state.saving),
    contentError: computed(() => state.error),
    ensureContentLoaded,
    loadContent,
    createContentSnapshot,
    saveContentDraft,
    uploadMediaFile,
    normalizeContentPayload,
    getDefaultContentSnapshot,
    createEmptyNavGroup,
    createEmptyNavLink,
    createEmptyTechNote,
    createEmptyLightVideo,
    createEmptyAudioTrack,
    createEmptySoftwareLink,
    createEmptyContentBlock,
    normalizeContentBlocks: normalizeContentBlocksClean,
    normalizeModuleIntros,
    normalizeLightVideos,
    normalizeAudioTracks,
    normalizeSoftwareLinks,
    renderContentBlocksToHtml,
    htmlToContentBlocks,
    editorTextToHtml,
    countCodeBlocks,
    countCodeBlocksInContentBlocks,
    slugify,
    ensureUniqueSlug,
    getBilibiliEmbedUrl,
    extractBilibiliVideoParts,
  };
};
